import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box } from "@mui/material";
import AppCardNav, { type CardNavGroup } from "./AppCardNav";
import { ModuleChromeProvider, type ModuleChromeSlot } from "./ModuleChromeContext";
import { useAuth } from "../contexts/AuthContext";
import { useMigrateLocalData } from "../lib/useMigrateLocalData";
import AccessRequestPrompt from './AccessRequestPrompt';

type RenderFn = (active?: string) => React.ReactNode;

const NAV: CardNavGroup[] = [
  {
    id: "equipment",
    title: "Оборудование",
    items: [
      { id: "equipment-list",     label: "Оборудование",   module: "equipment" },
      { id: "equipment-loans",    label: "Выдача/сдача",   module: "equipment-loans" },
      { id: "equipment-defective", label: "Дефектное",      module: "equipment-defective" },
      { id: "equipment-repair-queue", label: "К отгрузке в ремонт", module: "equipment-repair-queue" },
      { id: "equipment-shipments",label: "Отгрузки в ремонт",       module: "equipment-shipments" },
      { id: "equipment-shipped",  label: "Отгруженное",    module: "equipment-shipped" },
      { id: "equipment-transfers", label: "Передачи на другой ЛО", module: "equipment-transfers" },
    ],
  },
  {
    id: "assets",
    title: "Имущество",
    items: [
      { id: "assets-main", label: "Инвентаризация", module: "assets" },
    ],
  },
  {
    id: "tmc",
    title: "ТМЦ",
    items: [{ id: "tmc-calculator", label: "Калькулятор ТМЦ", module: "tmc-calculator" }],
  },
  {
    id: "flow",
    title: "Потоки работ",
    items: [
      { id: "incidents", label: "Проблемы участков", module: "incidents" },
      { id: "access-requests", label: "Запросы доступа", module: "access-requests" },
      { id: "job-profiles", label: "Должности и ответственность", module: "job-profiles" },
      { id: "work-schedule", label: "Рабочий график", module: "work-schedule" },
      { id: "shift-passport", label: "Паспорт смены", module: "shift-passport" },
      { id: "kanban", label: "Доска задач", module: "kanban" },
    ],
  },
  {
    id: "analytics",
    title: "Аналитика",
    items: [
      { id: "sort-performance", label: "Производительность сортировки", module: "sort-performance" },
      { id: "kpi-shifts", label: "KPI Смен · в разработке", module: "kpi-shifts" },
      { id: "analytics-main", label: "Сводная аналитика", module: "analytics" },
    ],
  },
  {
    id: "logistics",
    title: "Логистика",
    items: [
      { id: "parking-balance", label: "Баланс парковок", module: "parking-balance" },
    ],
  },
  {
    id: "safety",
    title: "Безопасность",
    items: [
      { id: "camera-map", label: "Карта камер", module: "camera-map" },
      { id: "audit5s", label: "Журнал 5S / ОТ", module: "audit5s" },
      { id: "visits",  label: "Посещения (КПП)", module: "visits" },
    ],
  },
];

const LS_ACTIVE  = "shell.active-module.v1";

const HIDDEN_MODULES = [
  "equipment-tsd",
  "equipment-finger_scanners",
  "equipment-desktop_scanners",
  "equipment-tablets",
  "equipment-pc_sets",
  "profile",
  "dashboard",
];

const ADMIN_GROUP: CardNavGroup = {
  id: "admin-section",
  title: "Администрирование",
  items: [
    { id: "admin-users", label: "Пользователи", module: "admin" },
  ],
};

export default function Shell({ children }: { children: RenderFn }) {
  useMigrateLocalData();
  const { hasAccess } = useAuth();
  const [active, setActive] = useState<string | undefined>(() => {
    try {
      return new URLSearchParams(window.location.search).get('module') || localStorage.getItem(LS_ACTIVE) || undefined;
    } catch {
      return undefined;
    }
  });
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [notificationMenuOpen, setNotificationMenuOpen] = useState(false);
  const [moduleChrome, setModuleChrome] = useState<ModuleChromeSlot | null>(null);
  const bodyOverflowRef = useRef<string | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(LS_ACTIVE, active ?? "");
    } catch {}
  }, [active]);

  const navGroups = useMemo(() => {
    const base = hasAccess("admin") ? [...NAV, ADMIN_GROUP] : NAV;
    return base
      .map((g) => ({ ...g, items: g.items.filter((i) => hasAccess(i.module)) }))
      .filter((g) => g.items.length > 0);
  }, [hasAccess]);

  const allowedModules = useMemo(() => {
    const fromNav = navGroups.flatMap((g) => g.items.map((i) => i.module));
    return new Set<string>([...fromNav, ...HIDDEN_MODULES]);
  }, [navGroups]);

  const fallbackModuleChrome = useMemo<ModuleChromeSlot | null>(() => {
    if (!active) return null;
    const activeItem = navGroups.flatMap((g) => g.items).find((item) => item.module === active);
    return activeItem ? { title: activeItem.label } : null;
  }, [active, navGroups]);

  const displayedModuleChrome = moduleChrome ?? fallbackModuleChrome;

  useEffect(() => {
    if (active && !allowedModules.has(active)) {
      setActive(undefined);
    }
  }, [active, allowedModules]);

  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const closeProfileMenu = useCallback(() => {
    setProfileMenuOpen(false);
  }, []);
  const closeNotificationMenu = useCallback(() => {
    setNotificationMenuOpen(false);
  }, []);
  const closeChrome = useCallback(() => {
    setMenuOpen(false);
    closeProfileMenu();
    closeNotificationMenu();
  }, [closeNotificationMenu, closeProfileMenu]);
  const toggleMenu = useCallback(() => {
    closeProfileMenu();
    closeNotificationMenu();
    setMenuOpen((curr) => !curr);
  }, [closeNotificationMenu, closeProfileMenu]);
  const handleProfileMenuOpenChange = useCallback((open: boolean) => {
    setProfileMenuOpen(open);
    if (open) {
      setMenuOpen(false);
      closeNotificationMenu();
    }
  }, [closeNotificationMenu]);
  const handleNotificationMenuOpenChange = useCallback((open: boolean) => {
    setNotificationMenuOpen(open);
    if (open) {
      setMenuOpen(false);
      closeProfileMenu();
    }
  }, [closeProfileMenu]);
  const chromeOverlayOpen = menuOpen || profileMenuOpen || notificationMenuOpen;

  const navigateTo = useCallback(
    (module: string) => {
      if (!allowedModules.has(module)) return;
      const url = new URL(window.location.href);
      if (url.searchParams.get('module') !== module) {
        if (!window.dispatchEvent(new Event('shell:before-navigate', { cancelable: true }))) return;
        url.searchParams.set('module', module);
        url.searchParams.delete('incident');
        window.history.pushState({}, '', url);
      }
      setActive(module);
      closeChrome();
      try {
        localStorage.setItem(LS_ACTIVE, module);
      } catch {}
    },
    [allowedModules, closeChrome],
  );

  const handleBrandClick = useCallback(() => {
    navigateTo("dashboard");
  }, [navigateTo]);

  useEffect(() => {
    const onPop = () => {
      const module = new URLSearchParams(window.location.search).get('module') || 'dashboard';
      if (module !== active && !window.dispatchEvent(new Event('shell:before-navigate', { cancelable: true }))) {
        const url = new URL(window.location.href);
        url.searchParams.set('module', active || 'dashboard');
        window.history.pushState({}, '', url);
        return;
      }
      if (allowedModules.has(module)) { setActive(module); closeChrome(); }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [active, allowedModules, closeChrome]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeChrome();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [closeChrome]);

  useEffect(() => {
    if (chromeOverlayOpen) {
      if (bodyOverflowRef.current === null) {
        bodyOverflowRef.current = document.body.style.overflow;
      }
      document.body.style.overflow = "hidden";
      return undefined;
    }

    if (bodyOverflowRef.current !== null) {
      document.body.style.overflow = bodyOverflowRef.current;
      bodyOverflowRef.current = null;
    }
    return undefined;
  }, [chromeOverlayOpen]);

  useEffect(() => {
    return () => {
      if (bodyOverflowRef.current !== null) {
        document.body.style.overflow = bodyOverflowRef.current;
        bodyOverflowRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const onLogout = () => {
      setActive(undefined);
      closeChrome();
    };
    window.addEventListener("auth:logout", onLogout);
    return () => window.removeEventListener("auth:logout", onLogout);
  }, [closeChrome]);

  useEffect(() => {
    const onNav = (e: Event) => {
      const ev = e as CustomEvent<{ module: string }>;
      const mod = ev.detail?.module;
      if (mod) navigateTo(mod);
    };
    window.addEventListener("shell:navigate", onNav as EventListener);
    return () => window.removeEventListener("shell:navigate", onNav as EventListener);
  }, [navigateTo]);

  return (
    <Box
      sx={{
        display: "flex",
        width: "100%",
        minHeight: "100vh",
        position: "relative",
        overflowX: "hidden",
        bgcolor: (t) =>
          t.palette.mode === "dark" ? "transparent" : "background.default",
        flexDirection: "column",
      }}
    >
      <ModuleChromeProvider setSlot={setModuleChrome}>
        <AppCardNav
          open={menuOpen}
          groups={navGroups}
          activeModule={active}
          onToggle={toggleMenu}
          onClose={closeMenu}
          onNavigate={navigateTo}
          onBrandClick={handleBrandClick}
          moduleChrome={displayedModuleChrome}
          profileMenuOpen={profileMenuOpen}
          onProfileMenuOpenChange={handleProfileMenuOpenChange}
          notificationMenuOpen={notificationMenuOpen}
          onNotificationMenuOpenChange={handleNotificationMenuOpenChange}
        />

        {/* Content */}
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            transition:
              "filter .18s ease, opacity .18s ease, transform .18s ease",
            filter: chromeOverlayOpen
              ? "blur(1.5px) contrast(0.78) saturate(0.82) brightness(0.9)"
              : "none",
            opacity: chromeOverlayOpen ? 0.82 : 1,
            transform: chromeOverlayOpen ? "scale(0.997)" : "none",
            pointerEvents: chromeOverlayOpen ? "none" : "auto",
            userSelect: chromeOverlayOpen ? "none" : "auto",
          }}
        >
          {children(active)}
        </Box>
      </ModuleChromeProvider>
      <AccessRequestPrompt />
    </Box>
  );
}
